import { useMemo } from 'react';
import { FormOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getBooleanAnswerOptions, paths } from '@app/constants';
import { translationsToFormValues } from '@app/utils';
import { Namespace, usePermissions } from '@data';
import { useQuestionDetails, useQuestionOptions } from '@data/hooks';
import { AnswerType, DependencyType, Language, QuestionDependencyView } from '@data/sdk/match';
import { Button, AntdConfigProvider, AntdSpace, AntdTag, AntdTypography } from '@ui/components';
import { COLOR_ERROR, COLOR_SUCCESS } from '@ui/constants';
import { ArrowUpRightIcon } from '@ui/icons';

const { Text } = AntdTypography;

export type QuestionDependencyProps = {
  answerType?: AnswerType;
  dependency: QuestionDependencyView;
  onEditClick: () => void;
  questionText: string;
};

export const BooleanDependencyPreview = ({
  answerType,
  dependency,
  questionText,
  onEditClick,
}: QuestionDependencyProps) => {
  const { t, i18n } = useTranslation([Namespace.ADMIN]);
  const { IS_REVIEWER } = usePermissions();

  const { data: sourceQuestion } = useQuestionDetails(dependency.sourceId);
  const isOptionsSource = sourceQuestion?.answerType === AnswerType.Options;
  const { data: sourceOptions } = useQuestionOptions(
    dependency.sourceId,
    { limit: 50, offset: 0 },
    isOptionsSource,
  );

  const sourceAnswerOptions = useMemo(
    () =>
      (sourceOptions?.items ?? []).map((option) => {
        const translations = translationsToFormValues(option.translations);
        return { label: translations[i18n.language as Language] || option.key, value: option.key };
      }),
    [sourceOptions, i18n.language],
  );

  const resolveApplicableAnswerLabel = (value: string | undefined) =>
    (isOptionsSource
      ? sourceAnswerOptions.find((v) => v.value === value)
      : getBooleanAnswerOptions().find((v) => v.value === value)
    )?.label ?? value;

  return (
    <AntdSpace
      orientation="vertical"
      size="small"
      align="center"
      className="pt-6 pb-12 [&>*]:max-w-[85%]"
    >
      <div className="flex flex-col gap-2 items-center justify-center p-4 bg-base-100 rounded-default border-solid border-base-300">
        <Text className="text-xl font-medium">{dependency.text}</Text>
        {!IS_REVIEWER && (
          <AntdSpace>
            <Button
              type="text"
              icon={<FormOutlined />}
              className="text-primary-600"
              onClick={onEditClick}
            >
              {t('patient:common.edit')}
            </Button>

            <Link
              className="text-primary-600"
              target="_blank"
              to={`/${paths.app}/${paths.questions_library.root}/${dependency.sourceId}/${paths.questions_library.details}`}
            >
              <ArrowUpRightIcon />
              {t('patient:common.open_in_new_tab')}
            </Link>
          </AntdSpace>
        )}
      </div>
      <div className="w-[1px] h-6 bg-base-600" />
      <AntdConfigProvider
        theme={{
          components: {
            Tag: { defaultBg: `${COLOR_SUCCESS}19`, colorBorder: `${COLOR_SUCCESS}50` },
          },
        }}
      >
        <AntdTag className="text-base-900 first-letter:capitalize">
          {dependency.type === DependencyType.HasDisease ? (
            <>
              {t('question_page.details.dependencies.has_disease_condition')}
              {dependency.parameters[0] && (
                <>
                  {' + '}
                  {t('question_page.details.dependencies.applicable_answer')}:{' '}
                  {resolveApplicableAnswerLabel(dependency.parameters[0])}
                </>
              )}
            </>
          ) : (
            <>
              {t('question_page.details.dependencies.applicable_answer')}:{' '}
              {resolveApplicableAnswerLabel(dependency.parameters[0])}
            </>
          )}
        </AntdTag>
      </AntdConfigProvider>
      <div className="w-[1px] h-6 bg-base-600" />
      <div className="p-4 bg-base-100 rounded-default border-solid border-primary-600">
        <Text className="text-xl font-medium">{questionText}</Text>
      </div>
      <div className="w-[1px] h-6 bg-base-600" />

      <AntdConfigProvider
        theme={{
          components: {
            Tag: { defaultBg: `${COLOR_ERROR}19`, colorBorder: `${COLOR_ERROR}50` },
          },
        }}
      >
        <AntdTag className="text-base-900">
          {t('question_page.details.dependencies.default_value_if_not_applicable')}:{' '}
          {answerType === AnswerType.Boolean
            ? getBooleanAnswerOptions().find((v) => v.value === dependency.parameters[1])?.label
            : dependency.parameters[1]}
        </AntdTag>
      </AntdConfigProvider>
    </AntdSpace>
  );
};
